import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

/**
 * SubscriptionSync - Simplified component for one-time subscription checks
 * 
 * NOTE: Subscription status is now automatically synced every minute by a global
 * scheduled job (sync-all-subscriptions). This component only handles:
 * 1. One-time check after returning from checkout (for immediate feedback)
 * 2. Checking for pending credit purchases (abandoned checkouts)
 * 
 * Users should read is_premium directly from the profiles table - it's always up-to-date.
 */
const SubscriptionSync = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    let syncing = false;

    const syncOnce = async () => {
      if (syncing) return;
      
      syncing = true;
      let userId: string | undefined;
      
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        userId = sessionData.session?.user?.id;
        
        if (!token || !userId) {
          console.log('[SubscriptionSync] No auth token, skipping');
          return;
        }

        // Only check for pending credit purchases (self-healing for abandoned checkouts)
        const pendingPromise = supabase.functions.invoke("check-pending-credit-purchases", {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        const pendingTimeout = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('check-pending-credit-purchases timeout')), 10000)
        );
        
        await Promise.race([pendingPromise, pendingTimeout]).catch(e => {
          console.error('[SubscriptionSync] Pending credits check failed/timeout:', e);
        });
        
        // Refetch queries to get latest data from DB (which is synced by global job)
        await queryClient.refetchQueries({ queryKey: ["user-credits", userId] });
        await queryClient.refetchQueries({ queryKey: ["subscription-status", userId] });
        await queryClient.refetchQueries({ queryKey: ["profile", userId] });
      } catch (e) {
        console.error("[SubscriptionSync] Sync failed", e);
      } finally {
        syncing = false;
      }
    };

    // One-time check on mount
    syncOnce();

    // Listen for forced sync events (e.g., after returning from checkout for immediate feedback)
    const handleForceSync = async () => {
      console.log('[SubscriptionSync] Force sync event - calling check-subscription for immediate feedback');
      
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        const userId = sessionData.session?.user?.id;
        
        if (!token || !userId) return;

        // Call check-subscription ONE TIME for immediate feedback after checkout
        const { data: subData, error: subError } = await supabase.functions.invoke("check-subscription", {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (subError) {
          console.error("[SubscriptionSync] check-subscription error:", subError);
        } else {
          console.log("[SubscriptionSync] Immediate subscription check completed", { 
            subscribed: subData?.subscribed,
            userId 
          });
        }

        // Try to grant daily credits immediately for new premium users
        await supabase.functions.invoke("grant-daily-credits", {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(e => {
          console.error('[SubscriptionSync] Credits grant failed:', e);
        });

        // Refetch all queries
        await queryClient.refetchQueries({ queryKey: ["user-credits", userId] });
        await queryClient.refetchQueries({ queryKey: ["subscription-status", userId] });
        await queryClient.refetchQueries({ queryKey: ["profile", userId] });

        // Dispatch event for components that need it
        if (subData && typeof subData.subscribed === "boolean") {
          window.dispatchEvent(new CustomEvent("subscription-synced", {
            detail: { userId, isPremium: subData.subscribed }
          }));
        }
      } catch (e) {
        console.error("[SubscriptionSync] Force sync failed", e);
      }
    };
    
    window.addEventListener("force-subscription-sync", handleForceSync);

    return () => {
      window.removeEventListener("force-subscription-sync", handleForceSync);
    };
  }, [queryClient]);

  return null;
};

export default SubscriptionSync;
